import { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { apiAccessRequest } from "../../constants";
import { useAlertContext, useUserContext } from "../../contexts";
import { BuildUrl } from "../../helpers";
import { AlertType, Holiday } from "../../models";
import { HolidayFormModal } from "./HolidayFormModal";
import { emptyFormData, HolidayFormData } from "./types";

const API_URL = BuildUrl("holiday");

type UpdateHolidayModalMode = "create" | "edit";

interface UpdateHolidayModalProps {
	open: boolean;
	mode: UpdateHolidayModalMode;
	holiday?: Holiday;
	onClose: (isUpdate: boolean) => void;
}

export const UpdateHolidayModal = ({
	open,
	mode,
	holiday,
	onClose
}: UpdateHolidayModalProps) => {
	const { showAlert } = useAlertContext();
	const { account } = useUserContext();
	const { instance } = useMsal();
	const [formData, setFormData] = useState<HolidayFormData>(emptyFormData);

	const isEditMode = mode === "edit";

	useEffect(() => {
		if (!isEditMode || !holiday) {
			if (!isEditMode) {
				setFormData(emptyFormData);
			}
			return;
		}

		const statesSet = new Set(
			holiday.clinics.flatMap((clinic) => clinic.state)
		);
		const selectedStates = [...statesSet].sort();
		const selectedClinicIds = holiday.clinics.map((clinic) => clinic.clinicId);

		setFormData({
			id: holiday.id,
			description: holiday.description,
			date: format(parse(holiday.date, "dd/MM/yyyy", new Date()), "yyyy-MM-dd"),
			recurring: holiday.recurring,
			states: selectedStates,
			clinicIds: selectedClinicIds
		});
	}, [holiday, isEditMode]);

	const handleClose = useCallback(() => {
		setFormData(emptyFormData);
		onClose(false);
	}, [onClose]);

	const handleSubmit = useCallback(async () => {
		if (
			(isEditMode && !holiday) ||
			!formData.description ||
			!formData.date ||
			formData.clinicIds.length === 0
		) {
			return;
		}

		const tokenResponse = await instance.acquireTokenSilent({
			account,
			scopes: apiAccessRequest.scopes
		});
		const headers = {
			Authorization: `Bearer ${tokenResponse.accessToken}`,
			"Content-Type": "application/json"
		};

		try {
			await fetch(API_URL, {
				method: isEditMode ? "PATCH" : "POST",
				headers,
				body: JSON.stringify(formData)
			});

			showAlert({
				message: `Holiday was ${isEditMode ? "updated" : "created"}`,
				alertType: AlertType.success,
				timeout: true
			});
			setFormData(emptyFormData);
			onClose(true);
		} catch (error) {
			console.error(
				`Failed to ${isEditMode ? "update" : "create"} holiday:`,
				error
			);
			showAlert({
				message: `Failed to ${isEditMode ? "update" : "create"} holiday`,
				alertType: AlertType.error
			});
		}
	}, [account, formData, holiday, instance, isEditMode, onClose, showAlert]);

	return (
		<HolidayFormModal
			open={open}
			title={isEditMode ? "Edit Holiday" : "Add Holiday"}
			submitLabel={isEditMode ? "Save" : "Add"}
			formData={formData}
			setFormData={setFormData}
			onClose={handleClose}
			onSubmit={handleSubmit}
		/>
	);
};
